import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuthSession, selfHelpApi, fileUploadRequest, getMediaUrl } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';

const AdminSelfHelpPage = () => {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // Modal State for Create / Edit
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [mediaUploadError, setMediaUploadError] = useState('');
  const [thumbnailUploadError, setThumbnailUploadError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    category: 'Anxiety & Stress',
    type: 'article',
    summary: '',
    content: '',
    mediaUrl: '',
    thumbnailUrl: '',
    readTime: '5 mins read',
    isPublished: true,
  });

  const categories = [
    'Anxiety & Stress',
    'Mindfulness & Meditation',
    'Depression Support',
    'Sleep Hygiene',
    'CBT Worksheets',
    'Self-Care Exercises',
    'General Wellness',
  ];

  const types = [
    { value: 'article', label: '📖 Article' },
    { value: 'audio', label: '🎵 Audio Guide' },
    { value: 'video', label: '🎥 Video Session' },
    { value: 'worksheet', label: '📝 Worksheet (PDF)' },
    { value: 'guide', label: '📚 Practical Guide' },
  ];

  const fetchResources = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await selfHelpApi.adminGetResources();
      setResources(res.resources || []);
    } catch (err) {
      console.error('Failed to fetch self-help resources:', err);
      setError(err.message || 'Failed to load self-help resources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchResources();
  }, [authSession?.token, navigate]);

  const handleOpenCreateModal = () => {
    setEditingItem(null);
    setMediaUploadError('');
    setThumbnailUploadError('');
    setFormData({
      title: '',
      category: 'Anxiety & Stress',
      type: 'article',
      summary: '',
      content: '',
      mediaUrl: '',
      thumbnailUrl: '',
      readTime: '5 mins read',
      isPublished: true,
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingItem(item);
    setMediaUploadError('');
    setThumbnailUploadError('');
    setFormData({
      title: item.title || '',
      category: item.category || 'Anxiety & Stress',
      type: item.type || 'article',
      summary: item.summary || '',
      content: item.content || '',
      mediaUrl: item.mediaUrl || '',
      thumbnailUrl: item.thumbnailUrl || '',
      readTime: item.readTime || '5 mins read',
      isPublished: item.isPublished !== undefined ? item.isPublished : true,
    });
    setShowModal(true);
  };

  const handleMediaFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMediaUploadError('');
    setUploadingMedia(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await fileUploadRequest('/upload/file', data);
      if (res?.url) {
        setFormData((prev) => ({ ...prev, mediaUrl: res.url }));
      } else {
        throw new Error(res?.message || 'File upload failed. No file URL returned.');
      }
    } catch (err) {
      console.error('Media upload error:', err);
      setMediaUploadError(err.message || 'Failed to upload media file');
    } finally {
      setUploadingMedia(false);
      e.target.value = '';
    }
  };

  const handleThumbnailFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbnailUploadError('');
    setUploadingThumbnail(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await fileUploadRequest('/upload/file', data);
      if (res?.url) {
        setFormData((prev) => ({ ...prev, thumbnailUrl: res.url }));
      } else {
        throw new Error(res?.message || 'Thumbnail upload failed. No image URL returned.');
      }
    } catch (err) {
      console.error('Thumbnail upload error:', err);
      setThumbnailUploadError(err.message || 'Failed to upload thumbnail image');
    } finally {
      setUploadingThumbnail(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingItem) {
        await selfHelpApi.adminUpdateResource(editingItem._id || editingItem.id, formData);
        alert('Self-Help resource updated successfully!');
      } else {
        await selfHelpApi.adminCreateResource(formData);
        alert('Self-Help resource created successfully!');
      }
      setShowModal(false);
      fetchResources();
    } catch (err) {
      alert(err.message || 'Failed to save self-help resource');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    try {
      await selfHelpApi.adminDeleteResource(id);
      setResources(resources.filter(r => (r._id || r.id) !== id));
      alert('Resource deleted successfully.');
    } catch (err) {
      alert(err.message || 'Failed to delete resource');
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      const res = await selfHelpApi.adminTogglePublish(id);
      setResources(resources.map(r => ((r._id || r.id) === id ? { ...r, isPublished: res.isPublished } : r)));
    } catch (err) {
      alert(err.message || 'Failed to update publish status');
    }
  };

  // Filter items
  const filtered = resources.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.summary.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    const matchesType = filterType === 'all' || item.type === filterType;
    return matchesSearch && matchesCategory && matchesType;
  });

  const stats = {
    total: resources.length,
    published: resources.filter(r => r.isPublished).length,
    drafts: resources.filter(r => !r.isPublished).length,
    articles: resources.filter(r => r.type === 'article').length,
    worksheets: resources.filter(r => r.type === 'worksheet').length,
  };

  return (
    <>
      <DashboardSidebar userRole="admin" />
      <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '40px 30px', marginLeft: '280px' }}>
        <div style={{ maxWidth: '1300px', margin: '0 auto' }}>
          {/* Back to dashboard */}
          <Link to="/admin-dashboard" style={{ color: '#667eea', fontWeight: 600, textDecoration: 'none', marginBottom: '16px', display: 'inline-block' }}>
            ← Back to Admin Dashboard
          </Link>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div>
              <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                💡 Self-Help Tools & Resources Management
              </h1>
              <p style={{ color: '#64748b', fontSize: '14px', marginTop: '6px' }}>
                Create, edit, delete, and publish self-help tools visible on the website frontend (`/self-help`).
              </p>
            </div>
            <button
              onClick={handleOpenCreateModal}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff',
                padding: '12px 22px',
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '14px',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              ➕ Add New Self-Help Tool
            </button>
          </div>

          {/* Stats Banner */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ background: '#ffffff', padding: '18px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Total Resources</span>
              <div style={{ fontSize: '24px', fontWeight: 800, color: '#0f172a', marginTop: '4px' }}>{stats.total}</div>
            </div>
            <div style={{ background: '#ffffff', padding: '18px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#059669', textTransform: 'uppercase' }}>Published</span>
              <div style={{ fontSize: '24px', fontWeight: 800, color: '#059669', marginTop: '4px' }}>{stats.published}</div>
            </div>
            <div style={{ background: '#ffffff', padding: '18px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#d97706', textTransform: 'uppercase' }}>Drafts</span>
              <div style={{ fontSize: '24px', fontWeight: 800, color: '#d97706', marginTop: '4px' }}>{stats.drafts}</div>
            </div>
            <div style={{ background: '#ffffff', padding: '18px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase' }}>Articles</span>
              <div style={{ fontSize: '24px', fontWeight: 800, color: '#2563eb', marginTop: '4px' }}>{stats.articles}</div>
            </div>
            <div style={{ background: '#ffffff', padding: '18px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase' }}>Worksheets</span>
              <div style={{ fontSize: '24px', fontWeight: 800, color: '#7c3aed', marginTop: '4px' }}>{stats.worksheets}</div>
            </div>
          </div>

          {/* Filter Bar */}
          <div style={{ background: '#ffffff', padding: '18px', borderRadius: '12px', marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', border: '1px solid #e2e8f0' }}>
            <input
              type="text"
              placeholder="🔍 Search self-help by title or summary..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1, minWidth: '240px', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
            />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff' }}
            >
              <option value="all">All Categories</option>
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff' }}
            >
              <option value="all">All Types</option>
              {types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          {/* Table */}
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading self-help resources...</div>
          ) : error ? (
            <div style={{ padding: '20px', background: '#fef2f2', color: '#ef4444', borderRadius: '8px' }}>{error}</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', color: '#64748b' }}>
              No self-help resources found. Click <strong>"Add New Self-Help Tool"</strong> to create your first resource!
            </div>
          ) : (
            <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 700 }}>
                    <th style={{ padding: '14px 18px' }}>Resource Title</th>
                    <th style={{ padding: '14px 18px' }}>Category</th>
                    <th style={{ padding: '14px 18px' }}>Read Time</th>
                    <th style={{ padding: '14px 18px' }}>Views</th>
                    <th style={{ padding: '14px 18px' }}>Status</th>
                    <th style={{ padding: '14px 18px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(item => {
                    const id = item._id || item.id;
                    return (
                      <tr key={id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '14px 18px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {item.thumbnailUrl && (
                              <img
                                src={getMediaUrl(item.thumbnailUrl)}
                                alt=""
                                style={{ width: '40px', height: '40px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #e2e8f0', flexShrink: 0 }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <div>
                              <div style={{ fontWeight: 700, color: '#0f172a' }}>{item.title}</div>
                              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ textTransform: 'uppercase', fontWeight: 700, fontSize: '11px', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '4px' }}>
                                  {item.type}
                                </span>
                                {item.mediaUrl && (
                                  <a
                                    href={getMediaUrl(item.mediaUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#059669', fontWeight: 600, fontSize: '11px', textDecoration: 'none' }}
                                  >
                                    🔗 Media File
                                  </a>
                                )}
                                <span>{item.summary ? `${item.summary.substring(0, 45)}...` : ''}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '14px 18px', color: '#334155', fontWeight: 600 }}>{item.category}</td>
                        <td style={{ padding: '14px 18px', color: '#64748b' }}>{item.readTime || '5 mins'}</td>
                        <td style={{ padding: '14px 18px', fontWeight: 700, color: '#2563eb' }}>👁️ {item.viewsCount || 0}</td>
                        <td style={{ padding: '14px 18px' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 700,
                            background: item.isPublished ? '#d1fae5' : '#fef3c7',
                            color: item.isPublished ? '#047857' : '#b45309'
                          }}>
                            {item.isPublished ? '✅ Published' : '⏳ Draft'}
                          </span>
                        </td>
                        <td style={{ padding: '14px 18px', textAlign: 'right' }}>
                          <button
                            onClick={() => handleTogglePublish(id)}
                            style={{
                              marginRight: '8px',
                              background: '#f8fafc',
                              border: '1px solid #cbd5e1',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: 600
                            }}
                          >
                            {item.isPublished ? 'Unpublish' : 'Publish'}
                          </button>
                          <button
                            onClick={() => handleOpenEditModal(item)}
                            style={{
                              marginRight: '8px',
                              background: '#2563eb',
                              color: '#fff',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: 600
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDelete(id, item.title)}
                            style={{
                              background: '#ef4444',
                              color: '#fff',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: 600
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* CREATE / EDIT MODAL */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            maxWidth: '750px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '28px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                {editingItem ? '✏️ Edit Self-Help Resource' : '➕ Create New Self-Help Tool'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Resource Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. 5-Minute Box Breathing for Anxiety Relief"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff' }}
                  >
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Resource Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff' }}
                  >
                    {types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Read / Duration Time
                  </label>
                  <input
                    type="text"
                    value={formData.readTime}
                    onChange={(e) => setFormData({ ...formData, readTime: e.target.value })}
                    placeholder="e.g. 5 mins read"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Short Card Summary *
                </label>
                <input
                  type="text"
                  required
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  placeholder="Brief preview summary displayed on the card..."
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Full Article / Guide Content *
                </label>
                <textarea
                  required
                  rows={6}
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter detailed guide content, exercises, instructions, or article text here..."
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontFamily: 'inherit' }}
                />
              </div>

              {/* File Uploads: Media & Thumbnail */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {/* File / Audio / Video Upload */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Upload File / Audio / Video (Optional)
                  </label>
                  
                  {formData.mediaUrl ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: '8px',
                      gap: '10px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                        <span style={{ fontSize: '20px' }}>
                          {formData.type === 'audio' ? '🎵' : formData.type === 'video' ? '🎥' : formData.type === 'worksheet' ? '📝' : '📄'}
                        </span>
                        <div style={{ overflow: 'hidden' }}>
                          <a
                            href={getMediaUrl(formData.mediaUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: '#059669',
                              textDecoration: 'underline',
                              display: 'block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '220px'
                            }}
                          >
                            {formData.mediaUrl.split('/').pop() || 'Uploaded Media'}
                          </a>
                          <span style={{ fontSize: '11px', color: '#16a34a' }}>✓ Uploaded file ready</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, mediaUrl: '' }))}
                        style={{
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 10px',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                      >
                        ✕ Remove
                      </button>
                    </div>
                  ) : (
                    <label style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '18px 12px',
                      background: uploadingMedia ? '#f1f5f9' : '#fafafa',
                      border: '2px dashed #cbd5e1',
                      borderRadius: '8px',
                      cursor: uploadingMedia ? 'not-allowed' : 'pointer',
                      textAlign: 'center',
                      transition: 'border-color 0.2s',
                    }}>
                      <input
                        type="file"
                        accept="audio/*,video/*,application/pdf,.pdf,.doc,.docx,.mp3,.wav,.m4a,.mp4,.webm"
                        disabled={uploadingMedia}
                        onChange={handleMediaFileUpload}
                        style={{ display: 'none' }}
                      />
                      <span style={{ fontSize: '24px', marginBottom: '4px' }}>
                        {uploadingMedia ? '⏳' : '📤'}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                        {uploadingMedia ? 'Uploading media file...' : 'Choose File / Audio / Video'}
                      </span>
                      <span style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                        MP3, WAV, MP4, PDF, DOC (Max 200MB)
                      </span>
                    </label>
                  )}
                  {mediaUploadError && (
                    <span style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px', fontWeight: 600 }}>
                      ⚠️ {mediaUploadError}
                    </span>
                  )}
                </div>

                {/* Thumbnail Image Upload */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Upload Thumbnail Image (Optional)
                  </label>

                  {formData.thumbnailUrl ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      gap: '10px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img
                          src={getMediaUrl(formData.thumbnailUrl)}
                          alt="Thumbnail Preview"
                          style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '6px',
                            objectFit: 'cover',
                            border: '1px solid #cbd5e1'
                          }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block' }}>
                            {formData.thumbnailUrl.split('/').pop() || 'Thumbnail image'}
                          </span>
                          <span style={{ fontSize: '11px', color: '#10b981' }}>✓ Image uploaded</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, thumbnailUrl: '' }))}
                        style={{
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 10px',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                      >
                        ✕ Remove
                      </button>
                    </div>
                  ) : (
                    <label style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '18px 12px',
                      background: uploadingThumbnail ? '#f1f5f9' : '#fafafa',
                      border: '2px dashed #cbd5e1',
                      borderRadius: '8px',
                      cursor: uploadingThumbnail ? 'not-allowed' : 'pointer',
                      textAlign: 'center',
                      transition: 'border-color 0.2s',
                    }}>
                      <input
                        type="file"
                        accept="image/*,.jpg,.jpeg,.png,.webp,.svg"
                        disabled={uploadingThumbnail}
                        onChange={handleThumbnailFileUpload}
                        style={{ display: 'none' }}
                      />
                      <span style={{ fontSize: '24px', marginBottom: '4px' }}>
                        {uploadingThumbnail ? '⏳' : '🖼️'}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                        {uploadingThumbnail ? 'Uploading image...' : 'Choose Thumbnail Image'}
                      </span>
                      <span style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                        PNG, JPG, WEBP, SVG (Max 10MB)
                      </span>
                    </label>
                  )}
                  {thumbnailUploadError && (
                    <span style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px', fontWeight: 600 }}>
                      ⚠️ {thumbnailUploadError}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px' }}>
                <input
                  type="checkbox"
                  id="isPublished"
                  checked={formData.isPublished}
                  onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                  style={{ width: '18px', height: '18px', accentColor: '#10b981', cursor: 'pointer' }}
                />
                <label htmlFor="isPublished" style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', cursor: 'pointer' }}>
                  Publish immediately (Visible to clients and website visitors)
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff',
                    border: 'none',
                    padding: '10px 24px',
                    borderRadius: '8px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(16,185,129,0.3)'
                  }}
                >
                  {submitting ? 'Saving...' : editingItem ? 'Update Tool' : 'Save & Publish Tool'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminSelfHelpPage;
